import useSWR from 'swr';
import { useMemo } from 'react';
import {
  DollarSign, ShoppingBag, Users, Inbox, ShoppingBasket,
  Building2, Repeat, TrendingUp, ArrowRight, Loader2,
} from 'lucide-react';
import {
  fetchAdminStats, fetchRevenueTimeline, fetchTopProducts, fetchRecentActivity,
} from '@/lib/api';

const AUD = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const AUD2 = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
  } catch { return iso.slice(0, 10); }
};

const fmtRelative = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return fmtDate(iso);
};

function StatCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="relative border border-white/10 bg-white/[0.02] p-5 hover:border-white/20 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">{label}</div>
          <div className="mt-3 font-display text-3xl sm:text-4xl text-white tabular-nums leading-none">{value}</div>
          {sub && <div className="mt-2 text-xs text-white/50">{sub}</div>}
        </div>
        <div className={`w-9 h-9 flex items-center justify-center border border-white/10 ${accent || 'text-white/70'}`}>
          <Icon size={16} />
        </div>
      </div>
    </div>
  );
}

function RevenueSparkline({ series }) {
  const { path, area, max } = useMemo(() => {
    if (!series?.length) return { path: '', area: '', max: 0 };
    const w = 600;
    const h = 120;
    const pad = 4;
    const values = series.map((d) => Number(d.revenue) || 0);
    const maxV = Math.max(1, ...values);
    const stepX = (w - pad * 2) / Math.max(1, series.length - 1);
    const points = values.map((v, i) => {
      const x = pad + i * stepX;
      const y = h - pad - ((v / maxV) * (h - pad * 2));
      return [x, y];
    });
    const p = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(' ');
    const a = `${p} L${points[points.length - 1][0]},${h - pad} L${points[0][0]},${h - pad} Z`;
    return { path: p, area: a, max: maxV };
  }, [series]);

  if (!series?.length) return null;
  return (
    <svg viewBox="0 0 600 120" preserveAspectRatio="none" className="w-full h-24" aria-label="Revenue last 30 days">
      <defs>
        <linearGradient id="mw-rev" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#9B111E" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#9B111E" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#mw-rev)" />
      <path d={path} fill="none" stroke="#9B111E" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      <text x="596" y="14" textAnchor="end" className="fill-white/40" style={{ font: '10px/1 -apple-system,Helvetica,Arial,sans-serif' }}>
        peak {AUD(max)}
      </text>
    </svg>
  );
}

const TYPE_STYLE = {
  order:   { color: 'text-emerald-400', label: 'ORDER' },
  inquiry: { color: 'text-sky-400',     label: 'INQUIRY' },
  quote:   { color: 'text-amber-400',   label: 'QUOTE' },
};

export default function OverviewPanel({ onNavigate }) {
  const { data: stats,    isLoading: sLoading } = useSWR('admin-stats', fetchAdminStats, { refreshInterval: 60000 });
  const { data: timeline, isLoading: tLoading } = useSWR('admin-revenue-timeline', () => fetchRevenueTimeline(30));
  const { data: top,      isLoading: pLoading } = useSWR('admin-top-products', () => fetchTopProducts(5));
  const { data: activity, isLoading: aLoading } = useSWR('admin-activity', () => fetchRecentActivity(15), { refreshInterval: 60000 });

  return (
    <div data-testid="admin-overview" className="space-y-10">
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">Dashboard</div>
        <h2 className="font-display font-light text-3xl sm:text-4xl mt-2">Trade console overview</h2>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard testId="stat-revenue-month" icon={DollarSign}
          label="Revenue · this month"
          value={sLoading ? '—' : AUD(stats?.revenue_month)}
          sub={sLoading ? '' : `${AUD(stats?.revenue_30d)} · last 30 days`}
          accent="text-[var(--mw-primary)]"
        />
        <StatCard testId="stat-orders-today" icon={ShoppingBag}
          label="Orders · today"
          value={sLoading ? '—' : (stats?.orders_today ?? 0)}
          sub={sLoading ? '' : `${stats?.orders_month ?? 0} this month`}
        />
        <StatCard testId="stat-customers" icon={Users}
          label="Customers"
          value={sLoading ? '—' : (stats?.customers_total ?? 0)}
          sub={sLoading ? '' : `${stats?.subs_active ?? 0} active subscriptions`}
        />
        <StatCard testId="stat-actions-needed" icon={Inbox}
          label="Needs action"
          value={sLoading ? '—' : ((stats?.inquiries_new ?? 0) + (stats?.wholesale_pending ?? 0) + (stats?.quotes_new ?? 0))}
          sub={sLoading ? '' : `${stats?.inquiries_new ?? 0} inquiries · ${stats?.wholesale_pending ?? 0} trade · ${stats?.quotes_new ?? 0} quotes`}
          accent="text-amber-400"
        />
      </div>

      {/* Revenue timeline */}
      <div className="border border-white/10 bg-white/[0.02] p-5 sm:p-6">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">Revenue · last 30 days</div>
            <div className="mt-2 font-display text-2xl sm:text-3xl text-white tabular-nums">
              {sLoading ? '—' : AUD(stats?.revenue_30d)}
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-emerald-400">
            <TrendingUp size={11} /> Live · paid orders
          </div>
        </div>
        {tLoading ? (
          <div className="h-24 flex items-center justify-center"><Loader2 size={16} className="animate-spin text-white/40" /></div>
        ) : (
          <RevenueSparkline series={timeline || []} />
        )}
      </div>

      {/* Two-col: top products + activity feed */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-4">
        {/* Top products */}
        <div className="border border-white/10 bg-white/[0.02] p-5 sm:p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">Top sellers</div>
              <div className="font-display text-xl mt-1">By revenue</div>
            </div>
            <button
              type="button"
              onClick={() => onNavigate?.('products')}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-white/50 hover:text-white transition-colors"
            >
              All products <ArrowRight size={11} />
            </button>
          </div>
          {pLoading ? (
            <div className="h-32 flex items-center justify-center"><Loader2 size={16} className="animate-spin text-white/40" /></div>
          ) : !top?.length ? (
            <div className="text-white/40 text-sm">No paid orders yet.</div>
          ) : (
            <ol className="space-y-3">
              {top.map((p, i) => (
                <li key={p.product_id} className="flex items-center gap-3">
                  <span className="font-display text-2xl text-white/25 tabular-nums w-6">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-white truncate">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mt-0.5">
                      {p.category} · {p.units} units
                    </div>
                  </div>
                  <div className="font-display text-white tabular-nums">{AUD(p.revenue)}</div>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Activity feed */}
        <div className="border border-white/10 bg-white/[0.02] p-5 sm:p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">Live feed</div>
              <div className="font-display text-xl mt-1">Recent activity</div>
            </div>
          </div>
          {aLoading ? (
            <div className="h-32 flex items-center justify-center"><Loader2 size={16} className="animate-spin text-white/40" /></div>
          ) : !activity?.length ? (
            <div className="text-white/40 text-sm">Nothing yet.</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {activity.map((row) => {
                const ts = TYPE_STYLE[row.type] || TYPE_STYLE.order;
                const targetTab = row.type === 'order' ? 'orders' : row.type === 'inquiry' ? 'inquiries' : 'quotes';
                return (
                  <li
                    key={`${row.type}-${row.id}`}
                    className="py-2.5 flex items-center gap-3 cursor-pointer hover:bg-white/[0.02] px-2 -mx-2 transition-colors"
                    onClick={() => onNavigate?.(targetTab)}
                  >
                    <span className={`text-[9px] uppercase tracking-[0.24em] ${ts.color} w-14 shrink-0`}>{ts.label}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-white truncate">{row.title}</div>
                      <div className="text-[11px] text-white/45 truncate">{row.detail}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 shrink-0">
                      {fmtRelative(row.created_at)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* Extra icons intentionally omitted from imports if unused: Repeat, Building2, ShoppingBasket */
